import { FAIconName, LabelWithIcon } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

type EventInfoType = "date" | "dateRange" | "nextDate" | "recurrence" | "time";

interface EventInfoItemProps {
  baseTestID: string;
  type: EventInfoType;
  value?: string;
}

interface EventInfoDisplayProps {
  icon: FAIconName;
  testID: string;
  valueTransform?: (value: string) => string;
}

const EventInfoMap: Record<EventInfoType, EventInfoDisplayProps> = {
  date: {
    icon: FAIconName.CalendarDay,
    testID: "date",
  },
  dateRange: {
    icon: FAIconName.ArrowsLeftRight,
    testID: "date-range",
  },
  nextDate: {
    icon: FAIconName.Forward,
    testID: "next-date",
    valueTransform: (value) => `Next on ${value}`,
  },
  recurrence: {
    icon: FAIconName.Refresh,
    testID: "recurrence",
  },
  time: {
    icon: FAIconName.Clock,
    testID: "time",
  },
};

const EventInfoItem = ({ baseTestID, type, value }: EventInfoItemProps) => {
  if (!isNotNilNotEmpty(value)) {
    return null;
  }

  const { icon, testID, valueTransform } = EventInfoMap[type];
  const displayLabel = valueTransform ? valueTransform(value) : value;

  const itemTestID = joinTestIDs(baseTestID, testID);
  return (
    <LabelWithIcon iconName={icon} testID={itemTestID} text={displayLabel} />
  );
};

interface EventDetailsLinesProps extends ViewWithTestID {
  dateRange?: string;
  isRecurring?: boolean;
  nextStartDate?: string;
  recurrenceString?: string;
  startDate?: string;
  timeRange?: string;
}

const EventDetailsLines = ({
  dateRange,
  isRecurring,
  nextStartDate,
  recurrenceString,
  startDate,
  testID,
  timeRange,
}: EventDetailsLinesProps) => {
  return (
    <VStack gap={4}>
      <EventInfoItem baseTestID={testID} type="date" value={startDate} />
      <EventInfoItem baseTestID={testID} type="time" value={timeRange} />
      {isRecurring && (
        <EventInfoItem
          baseTestID={testID}
          type="recurrence"
          value={recurrenceString}
        />
      )}
      <EventInfoItem
        baseTestID={testID}
        type="nextDate"
        value={nextStartDate}
      />
      <EventInfoItem baseTestID={testID} type="dateRange" value={dateRange} />
    </VStack>
  );
};

export type { EventDetailsLinesProps };
export { EventInfoMap };
export default EventDetailsLines;
